/**
 * Centralized state management for the Warehouse Layout Editor.
 * Replaces 13+ useState hooks with a single useReducer.
 * Includes undo/redo history for all layout mutations.
 */

import { useReducer, useMemo, useCallback } from 'react';
import {
  WarehouseLayout,
  RackPosition,
  RackDimensions,
  RackSummary,
  EditorState,
  ViewMode,
  PlacementConfig,
  PreviewRackPosition,
  HeatmapData,
  LocationActivity,
  LayoutMetrics,
  WarehouseObject,
  WarehouseObjectType,
  DEFAULT_EDITOR_STATE,
  DEFAULT_PLACEMENT_CONFIG,
} from '../types/warehouseLayout';
import { calculateRackDimensions } from '../utils/positionCalculator';
import { sortRacksByNumber } from '../utils/bulkPlacement';
import { serverGeometryToRackDimensions } from '../utils/layoutApiMapping';
import type {
  ServerRackGeometry,
  ServerBinPosition,
  ServerSpecialBin,
  ServerWarehouseInsights,
  ServerPendingMove,
} from '../../../../../services/warehouseLayoutService';

// ============================================
// Action Types
// ============================================

/** Fane i venstre sidepanel: Bygg (uplassert), Plassert, Innstillinger */
export type SidebarTab = 'build' | 'placed' | 'settings';

/** Aktivt innsikt-overlay: fargelegging av tvillingen basert på lagerdata */
export type InsightMode = 'none' | 'heatmap' | 'fill' | 'empty' | 'dead' | 'countage';

/** Mål for innholdsdialogen («hva ligger her?») — reol eller enkeltlokasjon */
export interface ContentsDialogTarget {
  kind: 'rack' | 'bin';
  code: string;
}

export type LayoutAction =
  | { type: 'LOAD_DATA_START' }
  | { type: 'LOAD_DATA_SUCCESS'; layout: WarehouseLayout; rackSummaries: RackSummary[]; serverGeometry?: ServerRackGeometry[]; binPositions?: ServerBinPosition[]; specialBins?: ServerSpecialBin[] }
  | { type: 'LOAD_DATA_FAILURE'; error: string; fallbackLayout: WarehouseLayout }
  | { type: 'SET_LAYOUT'; layout: WarehouseLayout; pushHistory?: boolean }
  | { type: 'ADD_RACK'; rack: RackPosition }
  | { type: 'ADD_RACKS'; racks: RackPosition[] }
  | { type: 'REMOVE_RACK'; rackNumber: string }
  | { type: 'ROTATE_RACK'; rackNumber: string }
  | { type: 'SET_RACK_POSITION'; rackNumber: string; x: number; y: number }
  | { type: 'SET_RACK_ROTATION'; rackNumber: string; rotation: 0 | 90 | 180 | 270 }
  | { type: 'SET_RACK_COLOR'; rackNumber: string; color: string | null }
  | { type: 'SET_RACK_LABEL'; rackNumber: string; label: string | null }
  | { type: 'MOVE_RACKS'; updates: Array<{ rackNumber: string; x: number; y: number }> }
  | { type: 'DELETE_SELECTED' }
  | { type: 'ROTATE_SELECTED' }
  | { type: 'ALIGN_SELECTED_TO_GRID' }
  | { type: 'SET_ANCHOR'; rackNumber: string | null }
  | { type: 'SET_SELECTION'; selectedRacks: Set<string> }
  | { type: 'PLACE_BIN'; position: ServerBinPosition }
  | { type: 'MOVE_BIN'; binCode: string; x: number; y: number }
  | { type: 'ROTATE_BIN'; binCode: string }
  | { type: 'SET_BIN_ROTATION'; binCode: string; rotation: number }
  | { type: 'SET_BIN_KIND'; binCode: string; kind: ServerBinPosition['placementKind'] }
  | { type: 'REMOVE_BIN'; binCode: string }
  | { type: 'SET_SELECTED_BIN'; binCode: string | null }
  | { type: 'SET_VIEW_MODE'; mode: ViewMode }
  | { type: 'TOGGLE_EDITOR_OPTION'; option: 'showGrid' | 'showLabels' | 'showActivity' }
  | { type: 'SET_INSIGHT_MODE'; mode: InsightMode }
  | { type: 'SET_INSIGHT_DAYS'; days: number }
  | { type: 'SET_INSIGHTS_START' }
  | { type: 'SET_INSIGHTS_SUCCESS'; insights: ServerWarehouseInsights | null }
  | { type: 'SET_INSIGHTS_FAILURE' }
  | { type: 'SET_PENDING_MOVES'; moves: ServerPendingMove[] }
  | { type: 'TOGGLE_PENDING_MOVES' }
  | { type: 'TOGGLE_ZONE_PANEL' }
  | { type: 'SET_ZOOM'; zoom: number }
  | { type: 'SET_PAN_OFFSET'; offset: { x: number; y: number } }
  | { type: 'SET_RUBBER_BAND'; rubberBand: EditorState['rubberBand'] }
  | { type: 'SET_PLACEMENT_CONFIG'; config: PlacementConfig }
  | { type: 'SET_PREVIEW_POSITIONS'; positions: PreviewRackPosition[] }
  | { type: 'APPLY_BULK_PLACEMENT'; positions: RackPosition[] }
  | { type: 'CANCEL_BULK_PLACEMENT' }
  | { type: 'TOGGLE_UNPLACED_SELECTION'; rackNumber: string }
  | { type: 'SELECT_ALL_UNPLACED' }
  | { type: 'DESELECT_ALL_UNPLACED' }
  | { type: 'TOGGLE_PLACEMENT_PANEL' }
  | { type: 'SET_ERROR'; error: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SAVE_START' }
  | { type: 'SAVE_SUCCESS'; at: string }
  | { type: 'SAVE_FAILURE' }
  | { type: 'SET_SIDEBAR_TAB'; tab: SidebarTab }
  | { type: 'UPDATE_DIMENSIONS'; field: 'length' | 'width' | 'height'; value: number }
  | { type: 'RESET_LAYOUT'; emptyLayout: WarehouseLayout }
  | { type: 'IMPORT_LAYOUT'; layout: WarehouseLayout }
  | { type: 'OPEN_QUICK_LAYOUT' }
  | { type: 'CLOSE_QUICK_LAYOUT' }
  | { type: 'APPLY_SMART_LAYOUT'; positions: RackPosition[]; metrics: LayoutMetrics }
  | { type: 'SET_SIDEBAR_DRAG_PREVIEW'; preview: PreviewRackPosition | null }
  | { type: 'OPEN_CONTENTS_DIALOG'; target: ContentsDialogTarget }
  | { type: 'CLOSE_CONTENTS_DIALOG' }
  | { type: 'START_CALIBRATION' }
  | { type: 'SET_CALIBRATION_POINT'; point: { x: number; y: number } }
  | { type: 'CANCEL_CALIBRATION' }
  | { type: 'APPLY_CALIBRATION'; length: number; width: number }
  | { type: 'START_MOVE'; fromBinCode: string; itemNo: string; itemDescription: string | null; maxQuantity: number }
  | { type: 'SET_MOVE_TARGET'; binCode: string | null }
  | { type: 'CANCEL_MOVE' }
  | { type: 'ADD_OBJECT'; object: WarehouseObject }
  | { type: 'MOVE_OBJECT'; id: string; x: number; y: number }
  | { type: 'UPDATE_OBJECT'; id: string; changes: Partial<WarehouseObject> }
  | { type: 'REMOVE_OBJECT'; id: string }
  | { type: 'SET_SELECTED_OBJECT'; id: string | null }
  | { type: 'START_OBJECT_PLACEMENT'; objectType: WarehouseObjectType }
  | { type: 'CANCEL_OBJECT_PLACEMENT' }
  | { type: 'UNDO' }
  | { type: 'REDO' };

/** To-punkts skala-kalibrering av plantegningen (kun aktiv i 2D) */
export interface CalibrationState {
  active: boolean;
  pointA: { x: number; y: number } | null;
  pointB: { x: number; y: number } | null;
}

const IDLE_CALIBRATION: CalibrationState = { active: false, pointA: null, pointB: null };

/**
 * «Flytt herfra»-flyt (steg 6): mål-velger-modus i 2D med filtrerte kandidater.
 * targetBinCode=null = velger fortsatt mål; satt = bekreftelsesdialogen er åpen.
 */
export interface MoveState {
  active: boolean;
  fromBinCode: string;
  itemNo: string;
  itemDescription: string | null;
  maxQuantity: number;
  targetBinCode: string | null;
}

// ============================================
// State
// ============================================

export interface LayoutEditorState {
  // Data
  layout: WarehouseLayout | null;
  rackSummaries: RackSummary[];
  unplacedRacks: RackSummary[];
  /** Datadrevne reoldimensjoner fra server (StcBinSize) — tom liste = kun heuristikk */
  serverGeometry: ServerRackGeometry[];
  /** Plasserte enkeltlokasjoner (gulv/vegg/heng/skap/dock) fra server */
  binPositions: ServerBinPosition[];
  /** Spesial-lokasjoner (utenfor reol-grid) med mål — brukes til rendering av binPositions */
  specialBins: ServerSpecialBin[];
  /** Valgt enkeltlokasjon (kun én seleksjonstype om gangen — reoler ELLER enkeltlokasjon) */
  selectedBinCode: string | null;
  /** Åpen innholdsdialog («hva ligger her?») — null = lukket */
  contentsDialogFor: ContentsDialogTarget | null;

  // Innsikt-overlay (fase C)
  /** Aktivt overlay — 'none' = vanlig fargelegging */
  insightMode: InsightMode;
  /** Dagvindu for aktivitet-heatmap (7/30/90) */
  insightDays: number;
  /** Sist hentede innsiktsdata (null når mode='none' eller henting feilet) */
  insights: ServerWarehouseInsights | null;
  insightsLoading: boolean;

  // Ventende flytt (åpne StcIntRelOrder-ordrer) — piler i 2D-kartet
  /** Åpne flytteordrer fra server (tom liste = ingen / henting feilet) */
  pendingMoves: ServerPendingMove[];
  /** Vis flytte-piler i 2D — PÅ som standard (lagersjefen skal se dem uten å lete) */
  showPendingMoves: boolean;

  /** Soneoversikt-panel («hvor har jeg plass?» som tall per sone) — AV som standard */
  showZonePanel: boolean;

  // UI state
  editorState: EditorState;
  saveStatus: 'idle' | 'saving' | 'saved' | 'error';
  /** Tidspunkt (ISO) for siste vellykkede lagring — vises i statuslinjen */
  lastSavedAt: string | null;
  loading: boolean;
  error: string | null;
  /** Aktiv fane i venstre sidepanel */
  sidebarTab: SidebarTab;

  // Bulk placement
  showPlacementPanel: boolean;
  selectedUnplacedRacks: string[];
  previewPositions: PreviewRackPosition[];

  // Smart layout
  showQuickLayout: boolean;
  layoutMetrics: LayoutMetrics | null;

  // Drag from sidebar
  sidebarDragPreview: PreviewRackPosition | null;

  // Skala-kalibrering av plantegning
  calibration: CalibrationState;

  // «Flytt herfra»-flyt (null = ikke aktiv)
  moveState: MoveState | null;

  /** Plasseringsmodus for objekter (søyle/port/stasjon/lading/sonemarkør) — null = ikke aktiv */
  objectPlacement: { type: WarehouseObjectType } | null;

  // History (undo/redo)
  history: WarehouseLayout[];
  historyIndex: number;
}

const MAX_HISTORY = 50;

export function createInitialState(): LayoutEditorState {
  return {
    layout: null,
    rackSummaries: [],
    unplacedRacks: [],
    serverGeometry: [],
    binPositions: [],
    specialBins: [],
    selectedBinCode: null,
    contentsDialogFor: null,
    insightMode: 'none',
    insightDays: 30,
    insights: null,
    insightsLoading: false,
    pendingMoves: [],
    showPendingMoves: true,
    showZonePanel: false,
    editorState: DEFAULT_EDITOR_STATE,
    saveStatus: 'idle',
    lastSavedAt: null,
    loading: true,
    error: null,
    sidebarTab: 'build',
    showPlacementPanel: false,
    selectedUnplacedRacks: [],
    previewPositions: [],
    showQuickLayout: false,
    layoutMetrics: null,
    calibration: IDLE_CALIBRATION,
    moveState: null,
    objectPlacement: null,
    sidebarDragPreview: null,
    history: [],
    historyIndex: -1,
  };
}

// ============================================
// Helpers
// ============================================

function computeUnplaced(layout: WarehouseLayout | null, summaries: RackSummary[]): RackSummary[] {
  if (!layout || summaries.length === 0) return [];
  const placedSet = new Set(layout.racks.map(r => r.rackNumber));
  return sortRacksByNumber(summaries.filter(s => !placedSet.has(s.rackNumber)));
}

function pushHistory(state: LayoutEditorState): LayoutEditorState {
  if (!state.layout) return state;
  // Trim future history if we branched
  const trimmed = state.history.slice(0, state.historyIndex + 1);
  const newHistory = [...trimmed, structuredClone(state.layout)].slice(-MAX_HISTORY);
  return {
    ...state,
    history: newHistory,
    historyIndex: newHistory.length - 1,
  };
}

function withLayout(
  state: LayoutEditorState,
  updater: (layout: WarehouseLayout) => WarehouseLayout,
  addHistory = true
): LayoutEditorState {
  if (!state.layout) return state;
  const stateWithHistory = addHistory ? pushHistory(state) : state;
  const newLayout = updater(stateWithHistory.layout!);
  return {
    ...stateWithHistory,
    layout: newLayout,
    unplacedRacks: computeUnplaced(newLayout, stateWithHistory.rackSummaries),
  };
}

// ============================================
// Reducer
// ============================================

export function layoutReducer(state: LayoutEditorState, action: LayoutAction): LayoutEditorState {
  switch (action.type) {
    // --- Data Loading ---
    case 'LOAD_DATA_START':
      return { ...state, loading: true, error: null };

    case 'LOAD_DATA_SUCCESS': {
      const unplaced = computeUnplaced(action.layout, action.rackSummaries);
      return {
        ...state,
        layout: action.layout,
        rackSummaries: action.rackSummaries,
        unplacedRacks: unplaced,
        serverGeometry: action.serverGeometry ?? [],
        binPositions: action.binPositions ?? [],
        specialBins: action.specialBins ?? [],
        loading: false,
        error: null,
        history: [structuredClone(action.layout)],
        historyIndex: 0,
      };
    }

    case 'LOAD_DATA_FAILURE':
      return {
        ...state,
        loading: false,
        error: action.error,
        layout: action.fallbackLayout,
      };

    // --- Layout Mutations ---
    case 'SET_LAYOUT': {
      const addHist = action.pushHistory !== false;
      return withLayout(state, () => action.layout, addHist);
    }

    case 'ADD_RACK':
      return withLayout(state, layout => ({
        ...layout,
        racks: [...layout.racks, action.rack],
      }));

    case 'ADD_RACKS':
      return withLayout(state, layout => ({
        ...layout,
        racks: [...layout.racks, ...action.racks],
      }));

    case 'REMOVE_RACK': {
      const s = withLayout(state, layout => ({
        ...layout,
        racks: layout.racks.filter(r => r.rackNumber !== action.rackNumber),
      }));
      const newSel = new Set(s.editorState.selectedRacks);
      newSel.delete(action.rackNumber);
      return {
        ...s,
        editorState: {
          ...s.editorState,
          selectedRacks: newSel,
          anchorRack: s.editorState.anchorRack === action.rackNumber ? null : s.editorState.anchorRack,
        },
      };
    }

    case 'ROTATE_RACK':
      return withLayout(state, layout => ({
        ...layout,
        racks: layout.racks.map(r =>
          r.rackNumber === action.rackNumber
            ? { ...r, rotation: ((r.rotation + 90) % 360) as 0 | 90 | 180 | 270 }
            : r
        ),
      }));

    // Direkte posisjon fra egenskaps-panelet (tallfelt) — med undo-historikk
    case 'SET_RACK_POSITION':
      return withLayout(state, layout => ({
        ...layout,
        racks: layout.racks.map(r =>
          r.rackNumber === action.rackNumber ? { ...r, x: action.x, y: action.y } : r
        ),
      }));

    // Eksplisitt rotasjon (0/90/180/270-knappene i egenskaps-panelet)
    case 'SET_RACK_ROTATION':
      return withLayout(state, layout => ({
        ...layout,
        racks: layout.racks.map(r =>
          r.rackNumber === action.rackNumber ? { ...r, rotation: action.rotation } : r
        ),
      }));

    // Egendefinert etikett (null/tom = tilbake til reolnummeret)
    case 'SET_RACK_LABEL':
      return withLayout(state, layout => ({
        ...layout,
        racks: layout.racks.map(r =>
          r.rackNumber === action.rackNumber ? { ...r, label: action.label ?? undefined } : r
        ),
      }));

    // Egendefinert farge (null = tilbake til standardfargen)
    case 'SET_RACK_COLOR':
      return withLayout(state, layout => ({
        ...layout,
        racks: layout.racks.map(r =>
          r.rackNumber === action.rackNumber ? { ...r, color: action.color ?? undefined } : r
        ),
      }));

    case 'MOVE_RACKS':
      return withLayout(state, layout => ({
        ...layout,
        racks: layout.racks.map(rack => {
          const update = action.updates.find(u => u.rackNumber === rack.rackNumber);
          return update ? { ...rack, x: update.x, y: update.y } : rack;
        }),
      }), false); // Don't push history on every mousemove - will push on mouseup

    case 'DELETE_SELECTED': {
      if (state.editorState.selectedRacks.size === 0) return state;
      const s = withLayout(state, layout => ({
        ...layout,
        racks: layout.racks.filter(r => !state.editorState.selectedRacks.has(r.rackNumber)),
      }));
      return {
        ...s,
        editorState: {
          ...s.editorState,
          selectedRacks: new Set(),
          anchorRack: state.editorState.selectedRacks.has(s.editorState.anchorRack || '') ? null : s.editorState.anchorRack,
        },
      };
    }

    case 'ROTATE_SELECTED': {
      if (state.editorState.selectedRacks.size === 0) return state;
      return withLayout(state, layout => ({
        ...layout,
        racks: layout.racks.map(rack =>
          state.editorState.selectedRacks.has(rack.rackNumber)
            ? { ...rack, rotation: ((rack.rotation + 90) % 360) as 0 | 90 | 180 | 270 }
            : rack
        ),
      }));
    }

    case 'ALIGN_SELECTED_TO_GRID': {
      if (!state.layout || state.editorState.selectedRacks.size === 0) return state;
      const gridUnit = state.layout.gridUnit;
      return withLayout(state, layout => ({
        ...layout,
        racks: layout.racks.map(rack =>
          state.editorState.selectedRacks.has(rack.rackNumber)
            ? {
                ...rack,
                x: Math.round(rack.x / gridUnit) * gridUnit,
                y: Math.round(rack.y / gridUnit) * gridUnit,
              }
            : rack
        ),
      }));
    }

    // --- Innholdsdialog («hva ligger her?») ---
    case 'OPEN_CONTENTS_DIALOG':
      return { ...state, contentsDialogFor: action.target };

    case 'CLOSE_CONTENTS_DIALOG':
      return { ...state, contentsDialogFor: null };

    // --- Skala-kalibrering av plantegning ---
    case 'START_CALIBRATION':
      return {
        ...state,
        calibration: { active: true, pointA: null, pointB: null },
        // Gjensidig utelukkende moduser: kalibrering avbryter flytte- og objektplasseringsmodus
        moveState: null,
        objectPlacement: null,
        // Rydd seleksjon så kalibrerings-klikkene ikke tolkes som redigering
        selectedBinCode: null,
        editorState: { ...state.editorState, selectedRacks: new Set(), anchorRack: null, selectedObject: null },
      };

    case 'SET_CALIBRATION_POINT': {
      if (!state.calibration.active) return state;
      if (!state.calibration.pointA) {
        return { ...state, calibration: { ...state.calibration, pointA: action.point } };
      }
      return { ...state, calibration: { ...state.calibration, pointB: action.point } };
    }

    case 'CANCEL_CALIBRATION':
      return { ...state, calibration: IDLE_CALIBRATION };

    // --- «Flytt herfra»-flyt (steg 6) ---
    case 'START_MOVE':
      return {
        ...state,
        moveState: {
          active: true,
          fromBinCode: action.fromBinCode,
          itemNo: action.itemNo,
          itemDescription: action.itemDescription,
          maxQuantity: action.maxQuantity,
          targetBinCode: null,
        },
        // Lukk innholdsdialogen som startet flyten
        contentsDialogFor: null,
        // Gjensidig utelukkende moduser: avbryt kalibrering, bulk- og objektplassering
        calibration: IDLE_CALIBRATION,
        objectPlacement: null,
        showPlacementPanel: false,
        selectedUnplacedRacks: [],
        previewPositions: [],
        // Rydd seleksjon slik at mål-klikkene ikke tolkes som redigering (som kalibrering)
        selectedBinCode: null,
        editorState: {
          ...state.editorState,
          // Mål-velgeren finnes kun i 2D
          viewMode: '2d',
          selectedRacks: new Set<string>(),
          anchorRack: null,
          selectedObject: null,
        },
      };

    case 'SET_MOVE_TARGET': {
      if (!state.moveState) return state;
      return { ...state, moveState: { ...state.moveState, targetBinCode: action.binCode } };
    }

    case 'CANCEL_MOVE':
      return { ...state, moveState: null };

    // --- Objekter (søyler, porter, stasjoner, lading, sonemarkører) ---
    case 'ADD_OBJECT':
      return withLayout(state, layout => ({
        ...layout,
        objects: [...layout.objects, action.object],
      }));

    // Drag i canvas — uten history per move (som MOVE_RACKS); autosave følger layout-endringen
    case 'MOVE_OBJECT':
      return withLayout(state, layout => ({
        ...layout,
        objects: layout.objects.map(o =>
          o.id === action.id ? { ...o, x: action.x, y: action.y } : o
        ),
      }), false);

    // Egenskaps-panelet (navn/type/mål/rotasjon/farge/posisjon) — med undo-historikk
    case 'UPDATE_OBJECT':
      return withLayout(state, layout => ({
        ...layout,
        objects: layout.objects.map(o =>
          o.id === action.id ? { ...o, ...action.changes } : o
        ),
      }));

    case 'REMOVE_OBJECT': {
      const s = withLayout(state, layout => ({
        ...layout,
        objects: layout.objects.filter(o => o.id !== action.id),
      }));
      return {
        ...s,
        editorState: {
          ...s.editorState,
          selectedObject: s.editorState.selectedObject === action.id ? null : s.editorState.selectedObject,
        },
      };
    }

    case 'SET_SELECTED_OBJECT':
      return {
        ...state,
        // Kun én seleksjonstype om gangen: objekt-valg tømmer reol- og enkeltlokasjon-valget
        selectedBinCode: action.id ? null : state.selectedBinCode,
        editorState: {
          ...state.editorState,
          selectedObject: action.id,
          selectedRacks: action.id ? new Set<string>() : state.editorState.selectedRacks,
        },
      };

    case 'START_OBJECT_PLACEMENT':
      return {
        ...state,
        objectPlacement: { type: action.objectType },
        // Gjensidig utelukkende moduser: avbryt kalibrering, flytte-modus og bulk-plassering
        calibration: IDLE_CALIBRATION,
        moveState: null,
        showPlacementPanel: false,
        selectedUnplacedRacks: [],
        previewPositions: [],
        // Rydd seleksjon slik at plasserings-klikkene ikke tolkes som redigering
        selectedBinCode: null,
        editorState: {
          ...state.editorState,
          // Plassering finnes kun i 2D
          viewMode: '2d',
          selectedRacks: new Set<string>(),
          anchorRack: null,
          selectedObject: null,
        },
      };

    case 'CANCEL_OBJECT_PLACEMENT':
      return { ...state, objectPlacement: null };

    case 'APPLY_CALIBRATION': {
      const s = withLayout(state, layout => ({
        ...layout,
        dimensions: {
          ...layout.dimensions,
          length: action.length,
          width: action.width,
        },
      }));
      return { ...s, calibration: IDLE_CALIBRATION };
    }

    case 'UPDATE_DIMENSIONS':
      return withLayout(state, layout => ({
        ...layout,
        dimensions: { ...layout.dimensions, [action.field]: action.value },
      }));

    case 'RESET_LAYOUT': {
      const s = pushHistory(state);
      return {
        ...s,
        layout: action.emptyLayout,
        unplacedRacks: computeUnplaced(action.emptyLayout, s.rackSummaries),
        editorState: { ...s.editorState, selectedRacks: new Set(), anchorRack: null },
      };
    }

    case 'IMPORT_LAYOUT': {
      const s = pushHistory(state);
      return {
        ...s,
        layout: action.layout,
        unplacedRacks: computeUnplaced(action.layout, s.rackSummaries),
      };
    }

    // --- Anchor & Selection ---
    case 'SET_ANCHOR':
      return {
        ...state,
        editorState: {
          ...state.editorState,
          anchorRack: action.rackNumber,
        },
      };

    case 'SET_SELECTION':
      return {
        ...state,
        // Kun én seleksjonstype om gangen: reol-valg nullstiller enkeltlokasjon- og objekt-valget
        selectedBinCode: action.selectedRacks.size > 0 ? null : state.selectedBinCode,
        editorState: {
          ...state.editorState,
          selectedRacks: action.selectedRacks,
          selectedObject: action.selectedRacks.size > 0 ? null : state.editorState.selectedObject,
        },
      };

    // --- Enkeltlokasjoner (BinPositions) — ikke dekket av undo/redo i v1 ---
    case 'PLACE_BIN': {
      if (state.binPositions.some(p => p.binCode === action.position.binCode)) return state;
      return { ...state, binPositions: [...state.binPositions, action.position] };
    }

    case 'MOVE_BIN':
      return {
        ...state,
        binPositions: state.binPositions.map(p =>
          p.binCode === action.binCode ? { ...p, positionX: action.x, positionY: action.y } : p
        ),
      };

    case 'ROTATE_BIN':
      return {
        ...state,
        binPositions: state.binPositions.map(p =>
          p.binCode === action.binCode ? { ...p, rotation: (p.rotation + 90) % 360 } : p
        ),
      };

    // Eksplisitt rotasjon fra egenskaps-panelet
    case 'SET_BIN_ROTATION':
      return {
        ...state,
        binPositions: state.binPositions.map(p =>
          p.binCode === action.binCode ? { ...p, rotation: action.rotation } : p
        ),
      };

    // Endre plasseringstype (gulv/vegg/heng/skap/dock) etter plassering
    case 'SET_BIN_KIND':
      return {
        ...state,
        binPositions: state.binPositions.map(p =>
          p.binCode === action.binCode ? { ...p, placementKind: action.kind } : p
        ),
      };

    case 'REMOVE_BIN':
      return {
        ...state,
        binPositions: state.binPositions.filter(p => p.binCode !== action.binCode),
        selectedBinCode: state.selectedBinCode === action.binCode ? null : state.selectedBinCode,
      };

    case 'SET_SELECTED_BIN':
      return {
        ...state,
        selectedBinCode: action.binCode,
        // Kun én seleksjonstype om gangen: enkeltlokasjon-valg tømmer reol- og objekt-valget
        editorState: action.binCode
          ? { ...state.editorState, selectedRacks: new Set<string>(), selectedObject: null }
          : state.editorState,
      };

    // --- Editor State ---
    case 'SET_VIEW_MODE':
      return {
        ...state,
        // Objektplassering finnes kun i 2D — bytte av visning avslutter modusen
        objectPlacement: action.mode === '2d' ? state.objectPlacement : null,
        editorState: { ...state.editorState, viewMode: action.mode },
      };

    case 'TOGGLE_EDITOR_OPTION':
      return {
        ...state,
        editorState: {
          ...state.editorState,
          [action.option]: !state.editorState[action.option],
        },
      };

    // --- Innsikt-overlay ---
    case 'SET_INSIGHT_MODE':
      return { ...state, insightMode: action.mode };

    case 'SET_INSIGHT_DAYS':
      return { ...state, insightDays: action.days };

    case 'SET_INSIGHTS_START':
      return { ...state, insightsLoading: true };

    case 'SET_INSIGHTS_SUCCESS':
      return { ...state, insights: action.insights, insightsLoading: false };

    case 'SET_INSIGHTS_FAILURE':
      return { ...state, insights: null, insightsLoading: false };

    // --- Ventende flytt (piler i 2D-kartet) ---
    case 'SET_PENDING_MOVES':
      return { ...state, pendingMoves: action.moves };

    case 'TOGGLE_PENDING_MOVES':
      return { ...state, showPendingMoves: !state.showPendingMoves };

    // --- Soneoversikt-panel («hvor har jeg plass?») ---
    case 'TOGGLE_ZONE_PANEL':
      return { ...state, showZonePanel: !state.showZonePanel };

    case 'SET_ZOOM':
      return {
        ...state,
        editorState: { ...state.editorState, zoom: action.zoom },
      };

    case 'SET_PAN_OFFSET':
      return {
        ...state,
        editorState: { ...state.editorState, panOffset: action.offset },
      };

    case 'SET_RUBBER_BAND':
      return {
        ...state,
        editorState: { ...state.editorState, rubberBand: action.rubberBand },
      };

    case 'SET_PLACEMENT_CONFIG':
      return {
        ...state,
        editorState: { ...state.editorState, placementConfig: action.config },
      };

    // --- Bulk Placement ---
    case 'SET_PREVIEW_POSITIONS':
      return { ...state, previewPositions: action.positions };

    case 'APPLY_BULK_PLACEMENT': {
      if (action.positions.length === 0) return state;
      const s = withLayout(state, layout => ({
        ...layout,
        racks: [...layout.racks, ...action.positions],
      }));
      return {
        ...s,
        selectedUnplacedRacks: [],
        previewPositions: [],
        showPlacementPanel: false,
        editorState: { ...s.editorState, anchorRack: null },
      };
    }

    case 'CANCEL_BULK_PLACEMENT':
      return {
        ...state,
        selectedUnplacedRacks: [],
        previewPositions: [],
        showPlacementPanel: false,
        editorState: { ...state.editorState, anchorRack: null },
      };

    case 'TOGGLE_UNPLACED_SELECTION': {
      const idx = state.selectedUnplacedRacks.indexOf(action.rackNumber);
      return {
        ...state,
        selectedUnplacedRacks:
          idx >= 0
            ? state.selectedUnplacedRacks.filter(r => r !== action.rackNumber)
            : [...state.selectedUnplacedRacks, action.rackNumber],
      };
    }

    case 'SELECT_ALL_UNPLACED':
      return {
        ...state,
        selectedUnplacedRacks: state.unplacedRacks.map(r => r.rackNumber),
      };

    case 'DESELECT_ALL_UNPLACED':
      return { ...state, selectedUnplacedRacks: [] };

    case 'TOGGLE_PLACEMENT_PANEL':
      return {
        ...state,
        showPlacementPanel: !state.showPlacementPanel,
        // Gjensidig utelukkende moduser: åpning av bulk-plassering avbryter flytte- og objektplasseringsmodus
        moveState: !state.showPlacementPanel ? null : state.moveState,
        objectPlacement: !state.showPlacementPanel ? null : state.objectPlacement,
      };

    // --- Smart Layout ---
    case 'OPEN_QUICK_LAYOUT':
      return { ...state, showQuickLayout: true };

    case 'CLOSE_QUICK_LAYOUT':
      return { ...state, showQuickLayout: false, layoutMetrics: null };

    case 'APPLY_SMART_LAYOUT': {
      if (action.positions.length === 0) return state;
      const s = withLayout(state, layout => ({
        ...layout,
        racks: [...layout.racks, ...action.positions],
      }));
      return {
        ...s,
        showQuickLayout: false,
        layoutMetrics: action.metrics,
        selectedUnplacedRacks: [],
        previewPositions: [],
      };
    }

    // --- Drag from Sidebar ---
    case 'SET_SIDEBAR_DRAG_PREVIEW':
      return { ...state, sidebarDragPreview: action.preview };

    // --- UI ---
    case 'SET_ERROR':
      return { ...state, error: action.error };

    case 'CLEAR_ERROR':
      return { ...state, error: null };

    case 'SAVE_START':
      return { ...state, saveStatus: 'saving' };

    case 'SAVE_SUCCESS':
      return { ...state, saveStatus: 'saved', lastSavedAt: action.at };

    case 'SAVE_FAILURE':
      return { ...state, saveStatus: 'error' };

    case 'SET_SIDEBAR_TAB':
      return { ...state, sidebarTab: action.tab };

    // --- Undo/Redo ---
    case 'UNDO': {
      if (state.historyIndex <= 0) return state;
      const newIndex = state.historyIndex - 1;
      const restored = structuredClone(state.history[newIndex]);
      return {
        ...state,
        layout: restored,
        historyIndex: newIndex,
        unplacedRacks: computeUnplaced(restored, state.rackSummaries),
      };
    }

    case 'REDO': {
      if (state.historyIndex >= state.history.length - 1) return state;
      const newIndex = state.historyIndex + 1;
      const restored = structuredClone(state.history[newIndex]);
      return {
        ...state,
        layout: restored,
        historyIndex: newIndex,
        unplacedRacks: computeUnplaced(restored, state.rackSummaries),
      };
    }

    default:
      return state;
  }
}

// ============================================
// Hook
// ============================================

export function useLayoutReducer() {
  const [state, dispatch] = useReducer(layoutReducer, undefined, createInitialState);

  const rackDimensionsMap = useMemo(() => {
    const map = new Map<string, RackDimensions>();

    // Heuristikk som grunnlag (dagens oppførsel) ...
    state.rackSummaries.forEach(summary => {
      map.set(summary.rackNumber, calculateRackDimensions(summary));
    });

    // ... overstyrt av datadrevne mål fra StcBinSize der serveren har dekning
    state.serverGeometry.forEach(geometry => {
      const dimensions = serverGeometryToRackDimensions(geometry);
      if (dimensions) {
        map.set(geometry.rackNumber, dimensions);
      }
    });

    return map;
  }, [state.rackSummaries, state.serverGeometry]);

  const canUndo = state.historyIndex > 0;
  const canRedo = state.historyIndex < state.history.length - 1;

  return { state, dispatch, rackDimensionsMap, canUndo, canRedo };
}
